import React from "react"
import { isHoliday } from "feiertagejs"
import { isHolidayToday } from "utils/holidays"
import Bowser from "bowser"
import { isbot } from "isbot"
import * as Sentry from "@sentry/browser"

// firebase
import { httpsCallable } from "firebase/functions"
import { Firestore, Functions } from "services/firebase"
import { collection, addDoc, serverTimestamp } from "firebase/firestore"

// config
import config from "config"

// utils
import { matchTimeRanges } from "utils"
import { useAppCheck } from "./appcheck"

const FunnelContext = React.createContext()

function useFunnel() {
  const context = React.useContext(FunnelContext)
  if (!context) {
    throw new Error(`useFunnel must be used within a FunnelProvider`)
  }

  const [funnel] = context.funnel

  return {
    initialized: !!funnel,
    add: (action) => {
      if (!funnel) {
        console.warn("Funnel not initialized yet")
        return
      }
      if (!action) {
        console.warn("Action is required to add to the funnel")
        return
      }

      Sentry.addBreadcrumb({
        category: "ui.click",
        message: action,
        level: "info",
      })

      addDoc(collection(Firestore, `funnels/${funnel}/actions`), {
        resto_key: config.key,
        created_at: serverTimestamp(),
        action,
      })
    },
  }
}

function FunnelProvider(props) {
  const { hasValidAppCheck, appCheckError } = useAppCheck()
  const [value, setValue] = React.useState()

  const funnelCreated = React.useRef(false)

  React.useEffect(() => {
    if (funnelCreated.current) return
    funnelCreated.current = true

    const asyncHandler = async () => {
      try {
        const userAgent = window.navigator.userAgent

        const browser = Bowser.parse(userAgent)
        const isBot = isbot(userAgent)

        const newFunnelObject = {
          version: process.env.version || null,
          created_at: serverTimestamp(),
          updated_at: null,
          resto_key: config.key,
          actions: [],
          actions_map: {},
          actions_appearance: {},
          actions_list: [],
          last_action: null,
          is_during_opening_times: matchTimeRanges(config.hours),
          is_holiday: isHolidayToday(),
          hour: new Date().getHours(),
          day_key: new Date().getDate(),
          month_key: new Date().getMonth() + 1, // Months are 0-indexed in JS
          year_key: new Date().getFullYear(),
          day_of_week: new Date().getDay(), // 0 = Sunday, 6 = Saturday

          // agent info
          agent_browser_name: browser.browser.name || null,
          agent_browser_version: browser.browser.version || null,
          agent_os_name: browser.os.name || null,
          agent_os_version: browser.os.version || null,
          agent_platform_type: browser.platform.type || null,
          agent_platform_vendor: browser.platform.vendor || null,
          agent_platform_model: browser.platform.model || null,
          agent_engine_name: browser.engine.name || null,
          agent_engine_version: browser.engine.version || null,
          agent_user_agent: userAgent || null,

          // appchecks
          agent_app_check_valid_token: hasValidAppCheck,
          agent_app_check_validation_error: appCheckError?.message || null,

          // bot detection
          agent_is_bot: isBot,
        }

        const newFunnel = await addDoc(
          collection(Firestore, "funnels"),
          newFunnelObject
        )

        console.log("✅ Initialized funnel")

        setValue(newFunnel.id)

        await addDoc(collection(Firestore, `funnels/${newFunnel.id}/actions`), {
          resto_key: config.key,
          created_at: serverTimestamp(),
          action: "page_view",
        })

        Sentry.addBreadcrumb({
          category: "ui.navigation",
          message: "page_view",
          level: "info",
        })
      } catch (error) {
        console.error("❌ Error initializing funnel", error)
        Sentry.captureException(error)
      }
    }

    asyncHandler()
  }, [hasValidAppCheck, appCheckError])

  const funnel = React.useMemo(() => [value, setValue], [value])

  return <FunnelContext.Provider value={{ funnel }} {...props} />
}

export { FunnelProvider, useFunnel }
